import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Image as ImageIcon, FileText, Mic, Trash2, MapPin, Paperclip, Sparkles, FolderOpen, X } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useOffline } from "@/context/OfflineContext";
import { useGate } from "@/context/GateContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import VaultSelector from "@/components/VaultSelector";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const FMT_ICON = { photo: ImageIcon, pdf: FileText, voice: Mic, other: Paperclip };

const TYPE_STYLE = {
  FACTS: { color: "var(--ek-primary)", border: "rgba(46,130,246,0.35)" },
  EVENT: { color: "var(--ek-warning)", border: "rgba(245,158,11,0.35)" },
  CHILD: { color: "var(--ek-child)", border: "rgba(167,139,250,0.4)" },
};

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const PROV_META = {
  source_documented: { label: "Source-documented", color: "var(--ek-secure)", border: "rgba(14,159,110,0.35)" },
  user_confirmed: { label: "User-confirmed", color: "var(--ek-secure)", border: "rgba(14,159,110,0.35)" },
  user_reported: { label: "User-reported", color: "var(--ek-primary)", border: "rgba(46,130,246,0.35)" },
  calculated: { label: "Calculated", color: "#A78BFA", border: "rgba(167,139,250,0.35)" },
  needs_review: { label: "Needs review", color: "#E4B34A", border: "rgba(228,179,74,0.4)" },
};


export default function Timeline({ incidents, evidence, reload, startNew = 0, isPro = false, isFamily = true, matterId = null, highlight = null, onHighlightDone }) {
  const { gate } = useGate();
  const { submit, pending } = useOffline();
  const [open, setOpen] = useState(false);
  const [entryType, setEntryType] = useState("FACTS");
  const [pureFact, setPureFact] = useState("");
  const [location, setLocation] = useState("");
  const [occurredAt, setOccurredAt] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [child, setChild] = useState({ mood: "", sleep: "", appetite: "", school: "" });
  const [patternsOpen, setPatternsOpen] = useState(false);
  const [patterns, setPatterns] = useState("");
  const [patternsBusy, setPatternsBusy] = useState(false);
  const [step, setStep] = useState("edit");
  const [vaultPickerOpen, setVaultPickerOpen] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [factOnly, setFactOnly] = useState("");
  const [needsSupport, setNeedsSupport] = useState([]);
  const [interpretation, setInterpretation] = useState([]);
  const [childImpact, setChildImpact] = useState([]);
  const [refineNote, setRefineNote] = useState("");

  useEffect(() => { if (startNew > 0) setOpen(true); }, [startNew]);

  useEffect(() => {
    if (!highlight) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="incident-${highlight}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ek-highlight");
        setTimeout(() => el.classList.remove("ek-highlight"), 2600);
      }
      onHighlightDone && onHighlightDone();
    }, 200);
    return () => clearTimeout(t);
  }, [highlight]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setPureFact(""); setLocation(""); setOccurredAt(""); setSelected([]); setEntryType("FACTS");
    setChild({ mood: "", sleep: "", appetite: "", school: "" });
    setStep("edit"); setFactOnly(""); setNeedsSupport([]); setInterpretation([]); setChildImpact([]); setRefineNote("");
  };

  const runReview = async () => {
    if (!pureFact.trim()) return;
    if (!gate()) return;
    setReviewBusy(true);
    try {
      const { data } = await api.post("/coach/refine", { note: pureFact });
      const fo = (data.fact_only || []).join("\n").trim();
      setFactOnly(fo || pureFact.trim());
      setNeedsSupport(data.needs_support || []);
      setInterpretation(data.interpretation || []);
      setChildImpact(data.child_impact || []);
      setRefineNote("");
    } catch (e) {
      setFactOnly(pureFact.trim());
      setNeedsSupport([]); setInterpretation([]); setChildImpact([]);
      setRefineNote(e.response?.data?.detail || "Justice is unavailable right now — you can record your entry as written.");
    } finally {
      setReviewBusy(false);
      setStep("review");
    }
  };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const create = async () => {
    if (!pureFact.trim()) return;
    if (!gate()) return;
    setBusy(true);
    const finalFact = (factOnly.trim() || pureFact.trim());
    const payload = {
      pure_fact: finalFact,
      fact_only: finalFact,
      original_narrative: pureFact.trim(),
      child_impact: childImpact,
      location,
      occurred_at: occurredAt ? new Date(occurredAt).toISOString() : null,
      evidence_ids: selected,
      entry_type: entryType,
      child_fields: entryType === "CHILD" ? child : null,
      matter_id: matterId,
    };
    const res = await submit({ kind: "incident", payload });
    setBusy(false);
    if (res.ok === false) { toast.error("Could not save entry"); return; }
    if (res.queued) toast.message("Saved offline — will sync when you're back online");
    else toast.success("Entry recorded");
    setOpen(false);
    resetForm();
    if (!res.queued) reload();
  };

  const remove = async (inc) => {
    try { await api.delete(`/incidents/${inc.id}`); toast.success("Entry removed"); reload(); }
    catch { toast.error("Delete failed"); }
  };

  const openPatterns = async () => {
    if (!gate()) return;
    setPatternsOpen(true);
    if (!isPro) return;
    setPatternsBusy(true);
    try { const { data } = await api.get("/child/patterns"); setPatterns(data.patterns); }
    catch (e) { setPatterns(e.response?.data?.detail || "Could not load patterns."); }
    finally { setPatternsBusy(false); }
  };

  const upgradeToPro = async () => {
    try {
      const { data } = await api.post("/subscription/checkout", { plan: "pro", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setPatternsOpen(false);
      toast.message(e.response?.data?.detail || "Manage your plan under the More tab.");
    }
  };

  const pendingIncidents = (pending || [])
    .filter((p) => p.kind === "incident" && (p.payload.matter_id || null) === (matterId || null))
    .map((p) => ({ id: "pending-" + p.id, pure_fact: p.payload.pure_fact, occurred_at: p.payload.occurred_at || new Date(p.ts).toISOString(), entry_type: p.payload.entry_type || "FACTS", location: p.payload.location, child_fields: p.payload.child_fields, evidence: [], _pending: true }));
  const displayIncidents = [...pendingIncidents, ...incidents];

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="ek-stamp inline-block mb-1">THE INCIDENT FEED</div>
          <h2 className="font-head text-2xl font-semibold tracking-tight">Timeline of facts</h2>
          <p className="text-sm mt-1" style={{ color: "var(--ek-text-secondary)" }}>
            {incidents.length} entr{incidents.length === 1 ? "y" : "ies"} · chronological record
          </p>
        </div>

        <div className="flex items-center gap-2">
          {isFamily && (
            <Button onClick={openPatterns} data-testid="behavior-patterns-btn" variant="outline" className="rounded-md border" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
              <Sparkles className="h-4 w-4 mr-2" style={{ color: "var(--ek-child)" }} /> Behavior Patterns
            </Button>
          )}

          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-incident-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
                <Plus className="h-4 w-4 mr-2" /> New entry
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
              <DialogHeader>
                <DialogTitle className="font-head font-semibold tracking-tight">
                  {step === "edit" ? "Tell me what happened" : "Review & confirm with Justice"}
                </DialogTitle>
                <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
                  {step === "edit"
                    ? "Capture what happened in your own words — Justice will help you refine it before saving."
                    : "Justice separated the observable facts from interpretation. Review the fact-only version, then confirm."}
                </DialogDescription>
              </DialogHeader>

              {step === "edit" ? (
              <div className="py-2 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="pf">Raw Contemporaneous Entry (your narrative)</Label>
                  <Textarea id="pf" data-testid="incident-fact-input" value={pureFact} onChange={(e) => setPureFact(e.target.value)} rows={3} className="bg-transparent resize-none" placeholder="Describe what happened in your own words. Justice will help strip out interpretation." />
                </div>

                <div className="space-y-2">
                  <Label>Entry type</Label>
                  <div className="flex gap-2">
                    {["FACTS", "EVENT", ...(isFamily ? ["CHILD"] : [])].map((t) => (
                      <button key={t} type="button" data-testid={`entry-type-${t}`} onClick={() => setEntryType(t)} className="ek-stamp" style={{ background: entryType === t ? "var(--ek-surface-hover)" : "transparent", color: entryType === t ? TYPE_STYLE[t].color : "var(--ek-steel)", borderColor: entryType === t ? TYPE_STYLE[t].border : "var(--ek-border)" }}>
                        {t === "CHILD" ? "CHILD OBSERVATION" : t}
                      </button>
                    ))}
                  </div>
                </div>

                {entryType === "CHILD" && (
                  <div className="grid grid-cols-2 gap-3 border rounded-lg p-3" style={{ borderColor: "rgba(167,139,250,0.3)", background: "rgba(167,139,250,0.05)" }} data-testid="child-fields">
                    {[["mood", "Mood"], ["sleep", "Sleep"], ["appetite", "Appetite"], ["school", "School"]].map(([k, l]) => (
                      <div key={k} className="space-y-2">
                        <Label>{l}</Label>
                        <Input data-testid={`child-${k}`} value={child[k]} onChange={(e) => setChild({ ...child, [k]: e.target.value })} className="bg-transparent" placeholder={l} />
                      </div>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="loc">Location</Label>
                    <Input id="loc" data-testid="incident-location-input" value={location} onChange={(e) => setLocation(e.target.value)} className="bg-transparent" placeholder="e.g. Front entrance" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="when">Date & time</Label>
                    <Input id="when" data-testid="incident-time-input" type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} className="bg-transparent" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Evidence</Label>
                  <Button type="button" variant="outline" onClick={() => setVaultPickerOpen(true)} data-testid="select-from-vault-btn" className="w-full rounded-md justify-start" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
                    <FolderOpen className="h-4 w-4 mr-2" style={{ color: "var(--ek-primary)" }} />
                    {selected.length > 0 ? `${selected.length} file${selected.length === 1 ? "" : "s"} attached — tap to change` : "Select from Vault"}
                  </Button>
                  {selected.length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid="selected-evidence-chips">
                      {selected.map((id) => {
                        const ev = evidence.find((e) => e.id === id);
                        const Icon = FMT_ICON[ev?.format] || Paperclip;
                        return (
                          <span key={id} className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs" style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)" }}>
                            <Icon className="h-3 w-3 shrink-0" style={{ color: "var(--ek-text-muted)" }} />
                            <span className="truncate max-w-[140px]" data-private>{ev?.filename || "Selected file"}</span>
                            <button type="button" onClick={() => toggle(id)} data-testid={`remove-evidence-${id}`} className="opacity-60 hover:opacity-100">
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              ) : (
              <div className="py-2 space-y-4" data-testid="review-panel">
                {refineNote && (
                  <div className="border rounded-md p-3 text-sm" style={{ background: "rgba(245,158,11,0.08)", borderColor: "rgba(245,158,11,0.25)", color: "var(--ek-text-secondary)" }} data-testid="review-note">
                    {refineNote}
                  </div>
                )}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4" style={{ color: "var(--ek-primary)" }} />
                    <Label>Court-Admissible Fact Extraction</Label>
                  </div>
                  <div className="rounded-md p-1" style={{ background: "rgba(46,130,246,0.10)", border: "1px solid rgba(46,130,246,0.35)" }}>
                    <Textarea data-testid="fact-only-input" value={factOnly} onChange={(e) => setFactOnly(e.target.value)} rows={4} className="bg-transparent resize-none border-0 focus-visible:ring-0" placeholder="Observable facts only." />
                  </div>
                  <p className="ek-mono-label" data-testid="original-preserved-note">YOUR RAW CONTEMPORANEOUS ENTRY IS PRESERVED WITH THIS RECORD</p>
                </div>

                {childImpact.length > 0 && (
                  <div className="space-y-1.5" data-testid="review-child-impact">
                    <div className="ek-stamp inline-block" style={{ color: "var(--ek-child)", borderColor: "rgba(167,139,250,0.35)" }}>CHILD IMPACT · MOOD / SLEEP / SCHEDULE</div>
                    <ul className="text-sm space-y-1 pl-1" style={{ color: "var(--ek-text-secondary)" }}>
                      {childImpact.map((s, i) => <li key={i}>• {s}</li>)}
                    </ul>
                  </div>
                )}

                {needsSupport.length > 0 && (
                  <div className="space-y-1.5" data-testid="review-needs-support">
                    <div className="ek-stamp inline-block" style={{ color: "var(--ek-warning)", borderColor: "rgba(245,158,11,0.35)" }}>NEEDS SUPPORT</div>
                    <ul className="text-sm space-y-1 pl-1" style={{ color: "var(--ek-text-secondary)" }}>
                      {needsSupport.map((s, i) => <li key={i}>• {s}</li>)}
                    </ul>
                  </div>
                )}
                {interpretation.length > 0 && (
                  <div className="space-y-1.5" data-testid="review-interpretation">
                    <div className="ek-stamp inline-block" style={{ color: "var(--ek-child)", borderColor: "rgba(167,139,250,0.35)" }}>PERSONAL OBSERVATION (KEPT SEPARATE)</div>
                    <ul className="text-sm space-y-1 pl-1" style={{ color: "var(--ek-text-secondary)" }}>
                      {interpretation.map((s, i) => <li key={i}>• {s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
              )}

              <DialogFooter>
                {step === "edit" ? (
                  <Button onClick={runReview} disabled={reviewBusy || !pureFact.trim()} data-testid="review-incident-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
                    <Sparkles className="h-4 w-4 mr-2" /> {reviewBusy ? "REVIEWING..." : "Review with Justice · 1 KeeperCredit™"}
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 w-full justify-between">
                    <Button onClick={() => setStep("edit")} variant="outline" data-testid="review-back-btn" className="rounded-md border" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
                      Back to edit
                    </Button>
                    <Button onClick={create} disabled={busy || !factOnly.trim()} data-testid="save-incident-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
                      {busy ? "SAVING..." : "Confirm & record entry"}
                    </Button>
                  </div>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {displayIncidents.length === 0 ? (
        <div className="border border-dashed rounded-lg p-16 text-center" style={{ borderColor: "var(--ek-border)" }}>
          <p style={{ color: "var(--ek-text-secondary)" }}>No entries yet. Record your first entry to build the timeline.</p>
        </div>
      ) : (
        <div className="relative pl-6" data-testid="timeline-list">
          <div className="absolute left-[3px] top-2 bottom-2 w-px" style={{ background: "var(--ek-border)" }} />
          <div className="space-y-4">
            {displayIncidents.map((inc, i) => {
              const ts = TYPE_STYLE[inc.entry_type] || TYPE_STYLE.FACTS;
              const cf = inc.child_fields || {};
              return (
                <motion.div key={inc.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.04 }} className="relative" data-testid={`incident-${inc.id}`}>
                  <span className="absolute -left-6 top-1.5 h-2 w-2 rounded-full ek-glow" style={{ background: ts.color }} />
                  <div className="border rounded-lg" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
                    <div className="flex items-center justify-between gap-4 px-5 py-2.5 border-b ek-scanline" style={{ borderColor: "var(--ek-border)" }}>
                      <div className="flex items-center gap-2">
                        <span className="ek-stamp">RECORD #{inc.id.slice(-6).toUpperCase()}</span>
                        <span className="ek-stamp" style={{ color: ts.color, borderColor: ts.border }}>{inc.entry_type === "CHILD" ? "CHILD" : (inc.entry_type || "FACTS")}</span>
                        {inc.provenance && PROV_META[inc.provenance] && (
                          <span className="ek-stamp" data-testid={`provenance-${inc.id}`} title="Source attribution"
                            style={{ color: PROV_META[inc.provenance].color, borderColor: PROV_META[inc.provenance].border }}>
                            {PROV_META[inc.provenance].label}
                          </span>
                        )}
                        {inc._pending && <span className="ek-stamp" data-testid="pending-badge" style={{ color: "var(--ek-warning)", borderColor: "rgba(245,158,11,0.4)" }}>SAVED OFFLINE</span>}
                      </div>
                      {!inc._pending && (
                        <button onClick={() => remove(inc)} data-testid={`delete-incident-${inc.id}`} className="text-slate-500 hover:text-red-400 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="ek-mono-label mb-2">{fmtTime(inc.occurred_at)}</div>
                      <p className="text-base leading-relaxed" data-private>{inc.pure_fact}</p>
                      {inc.entry_type === "CHILD" && (cf.mood || cf.sleep || cf.appetite || cf.school) && (
                        <div className="mt-3 flex flex-wrap gap-2" data-private>
                          {[["Mood", cf.mood], ["Sleep", cf.sleep], ["Appetite", cf.appetite], ["School", cf.school]].filter(([, v]) => v).map(([l, v]) => (
                            <span key={l} className="ek-stamp" style={{ color: "var(--ek-child)", borderColor: "rgba(167,139,250,0.3)" }}>{l}: {v}</span>
                          ))}
                        </div>
                      )}
                      {inc.location && (
                        <div className="mt-2 flex items-center gap-1.5 text-sm" style={{ color: "var(--ek-text-secondary)" }}>
                          <MapPin className="h-3.5 w-3.5" /> <span data-private>{inc.location}</span>
                        </div>
                      )}
                      {inc.evidence?.length > 0 && (
                        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {inc.evidence.map((ev) => {
                            const Icon = FMT_ICON[ev.format] || Paperclip;
                            const dateFlag = ev.date_review_needed ? "Date Review" : (ev.date_status === "unconfirmed" ? "Unconfirmed" : null);
                            return (
                              <div key={ev.id} className="flex items-center gap-2 border rounded-md px-2.5 py-2" style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)" }}>
                                <Icon className="h-4 w-4 shrink-0" style={{ color: "var(--ek-text-muted)" }} />
                                <span className="text-xs truncate" data-private>{ev.filename}</span>
                                {dateFlag && (
                                  <span className="ek-mono-label shrink-0 px-1.5 py-0.5 rounded" data-testid={`timeline-date-flag-${ev.id}`}
                                    style={{ color: ev.date_review_needed ? "var(--ek-error)" : "#E4B34A", border: `1px solid ${ev.date_review_needed ? "rgba(240,97,109,0.35)" : "rgba(228,179,74,0.35)"}` }}>
                                    {dateFlag}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={patternsOpen} onOpenChange={setPatternsOpen}>
        <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
          {isPro ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-head font-semibold tracking-tight">Child Impact Intelligence</DialogTitle>
                <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>Environmental patterns Justice observed across your private Child Observations.</DialogDescription>
              </DialogHeader>
              <div className="py-2" data-testid="patterns-result">
                {patternsBusy
                  ? <p className="ek-mono-label ek-pulse">ANALYZING PATTERNS...</p>
                  : <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans" style={{ color: "var(--ek-text-secondary)" }} data-private>{patterns || "No patterns yet."}</pre>}
              </div>
            </>
          ) : (
            <div className="text-center py-4" data-testid="pro-nudge">
              <div className="h-12 w-12 mx-auto mb-4 rounded-lg flex items-center justify-center" style={{ background: "rgba(167,139,250,0.12)" }}>
                <Sparkles className="h-6 w-6" style={{ color: "var(--ek-child)" }} />
              </div>
              <DialogTitle className="font-head font-semibold tracking-tight text-xl mb-2">Unlock Child Impact Intelligence</DialogTitle>
              <p className="text-sm mb-6" style={{ color: "var(--ek-text-secondary)" }}>Upgrade to Litigator Pro to unlock Child Impact Intelligence and professional GAL-ready reports.</p>
              <Button data-testid="pro-upgrade-btn" onClick={() => setPatternsOpen(false)} className="rounded-md" style={{ background: "var(--ek-child)", color: "#0b0b14" }}>Upgrade to Litigator Pro — $44.99</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <VaultSelector
        open={vaultPickerOpen}
        onOpenChange={setVaultPickerOpen}
        matterId={matterId}
        multiple
        initialSelected={selected}
        onConfirm={(ids) => setSelected(ids)}
      />
    </div>
  );
}
